import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Hero from "./components/Hero";
import Strengths from "./components/Strengths";
import AboutPreview from "./components/AboutPreview";
import LatestProducts from "./components/LatestProducts";
import CtaBanner from "./components/CtaBanner";
import { siteInfo } from "@/mocks/site";
import { supabase } from "@/lib/supabase";

interface Notice {
  id: string;
  title: string;
  date: string;
  important: boolean;
}

interface TechDownload {
  id: string;
  title: string;
  category: string;
  file_size: string;
  date: string;
}

export default function Home() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [techDownloads, setTechDownloads] = useState<TechDownload[]>([]);

  useEffect(() => {
    supabase
      .from("notices")
      .select("id, title, date, important")
      .order("date", { ascending: false })
      .limit(4)
      .then(({ data }) => { if (data) setNotices(data as Notice[]); });

    supabase
      .from("technical_downloads")
      .select("id, title, category, file_size, date")
      .order("date", { ascending: false })
      .limit(4)
      .then(({ data }) => { if (data) setTechDownloads(data as TechDownload[]); });
  }, []);

  return (
    <SiteLayout navVariant="overlay">
      <h1 className="sr-only">
        한국고무공업주식회사 - 산업용 고무제품 · 맞춤형 고무성형 · 압축성형 전문 제조기업
      </h1>

      <Hero />
      <Strengths />

      {/* Quick Contact Bar */}
      <section className="py-10 bg-background-100 border-y border-background-200/70">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
            <p className="text-sm font-semibold text-foreground-600 whitespace-nowrap">
              고무제품에 문제가 있으신가요? 저희가 해결해 드리겠습니다.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <a
                href={siteInfo.telLink}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line" />
                </span>
                {siteInfo.tel}
              </a>
              <a
                href={siteInfo.emailLink}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-background-300 text-foreground-700 hover:bg-background-100 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line" />
                </span>
                {siteInfo.email}
              </a>
              <Link
                to="/quotation"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-background-50 hover:bg-accent-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-file-text-line" />
                </span>
                견적 요청
              </Link>
            </div>
          </div>
        </div>
      </section>

      <AboutPreview />

      {/* Quality & Technology Section */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-bold tracking-wider uppercase mb-4">
              Quality &amp; Technology
            </span>
            <h2 className="text-2xl md:text-4xl font-heading font-extrabold text-foreground-950">
              품질과 기술력으로 증명하는 신뢰
            </h2>
            <p className="mt-3 text-sm text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              50년간 축적된 압축성형 기술과 엄격한 품질관리 시스템으로 고객이 신뢰할 수 있는 제품을 공급합니다.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: "ri-shield-check-line",
                title: "ISO 9001 / IATF 16949",
                desc: "국제 품질경영시스템 인증으로 일관된 품질 보증",
              },
              {
                icon: "ri-flask-line",
                title: "연구개발전담부서",
                desc: "KOITA 인증 기업부설연구소 운영, 지속적 기술 혁신",
              },
              {
                icon: "ri-building-2-line",
                title: "2,000㎡ 생산시설",
                desc: "50톤~1300톤 프레스 30기, 자체 혼련 라인 보유",
              },
              {
                icon: "ri-global-line",
                title: "SINCE 1975",
                desc: "50년 이상의 업력",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="p-6 rounded-2xl bg-background-50 border border-background-200/70 text-center hover:border-primary-200/60 transition-colors"
              >
                <span className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto">
                  <i className={`${item.icon} text-primary-600 text-lg`} />
                </span>
                <h3 className="mt-4 font-heading font-bold text-sm text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LatestProducts />

      {/* Recent Notices + Tech Downloads */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            {/* Notices */}
            <div className="flex-1">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <span className="inline-block text-xs font-bold text-foreground-500 uppercase tracking-wider mb-2">
                    Notice
                  </span>
                  <h3 className="text-xl font-heading font-extrabold text-foreground-950">최근 공지사항</h3>
                </div>
                <Link
                  to="/support"
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer inline-flex items-center gap-1 whitespace-nowrap"
                >
                  전체보기
                  <i className="ri-arrow-right-line text-[10px]" />
                </Link>
              </div>
              <div className="flex flex-col gap-2">
                {notices.map((notice) => (
                  <Link
                    key={notice.id}
                    to="/support"
                    className="flex items-center justify-between gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200/60 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {notice.important && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-bold flex-shrink-0">
                          중요
                        </span>
                      )}
                      <span className="text-sm text-foreground-800 truncate">{notice.title}</span>
                    </div>
                    <span className="text-xs text-foreground-400 flex-shrink-0">{notice.date}</span>
                  </Link>
                ))}
                {notices.length === 0 && (
                  <p className="text-sm text-foreground-400 text-center py-8">등록된 공지사항이 없습니다.</p>
                )}
              </div>
            </div>

            {/* Tech Downloads */}
            <div className="flex-1">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <span className="inline-block text-xs font-bold text-foreground-500 uppercase tracking-wider mb-2">
                    Technical
                  </span>
                  <h3 className="text-xl font-heading font-extrabold text-foreground-950">최근 기술자료</h3>
                </div>
                <Link
                  to="/technical"
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer inline-flex items-center gap-1 whitespace-nowrap"
                >
                  전체보기
                  <i className="ri-arrow-right-line text-[10px]" />
                </Link>
              </div>
              <div className="flex flex-col gap-2">
                {techDownloads.map((file) => (
                  <Link
                    key={file.id}
                    to="/technical"
                    className="flex items-center justify-between gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200/60 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-pdf-2-line text-primary-600 text-sm" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm text-foreground-800 truncate">{file.title}</p>
                        <p className="text-xs text-foreground-400 mt-0.5">
                          {file.category} · {file.file_size}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-foreground-400 flex-shrink-0">{file.date}</span>
                  </Link>
                ))}
                {techDownloads.length === 0 && (
                  <p className="text-sm text-foreground-400 text-center py-8">등록된 기술자료가 없습니다.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner />
    </SiteLayout>
  );
}